// infrastructure/mappers/fundingSourceMapper.js

/**
 * Reads and writes the stored representation of this context's own FundingSource.
 *
 * NOT an anti-corruption layer — nothing here belongs to another bounded context. A funding source is
 * a payer, and payers are Revenue Cycle's own subject matter. This is the ordinary mapping between a
 * concept and the shape it is stored in.
 *
 * THIS FILE IS WHY THE STORED DOCUMENT KEEPS `fundingSourceName`. The platform spells a record's own
 * name `<entity>Name` everywhere — programName, sessionName, clinicName — and most readers of a
 * funding source live outside this context, in that convention. Renaming at rest would have bought
 * context-local tidiness by forking the persisted-layer convention per context. Mapping it here gets
 * the model its clean `name` for free, which is precisely the job a mapper exists to do.
 *
 * `validTreatmentServices` ↔ `coveredServices`. The document keeps the platform's spelling and its
 * `{ id }` entry key, because the funder document has always stored covered services that way and
 * readers outside this context still expect it; the model says `treatmentServiceId`, because an entry
 * references a treatment service rather than being one.
 *
 * MAPPED FIELD BY FIELD, NEVER SPREAD, and that is the whole point of this pair. The legacy editor
 * resolved each entry against the treatment-service list for display and handed the resolved row back
 * on save, so every write embedded a full treatment-service snapshot — serviceName, serviceCode,
 * isBillable, the service's own clinic and timestamps — into the funder's config. Naming each field
 * on the way out means a version cannot carry a snapshot in, whatever it is handed.
 *
 * The cost is that a per-service override must be added HERE as well as to the model, or it round-
 * trips to nothing: read drops it, write does not restore it, and a save silently erases config the
 * user entered. A spread would avoid that and reintroduce the snapshot bug, which is the worse of the
 * two. `coveredServiceFields` is the one list to extend.
 *
 * `status` — an inactivate-rather-than-delete string — becomes `isActive`.
 *
 * PRE-BACKFILL DOCUMENTS READ AS THEIR OWN SERIES HEAD. A funder written before versioning has no
 * `fundingSourceId`, `version` or `isLatestVersion`, so those default to itself, 1, and true — which
 * is exactly what the backfill writes, so such a document reads the same before and after. The one
 * field with no honest default is `effectiveFrom`: there is no record of when an unversioned funder
 * started applying, and inventing one would put a date on the timeline that nobody entered. It stays
 * null, and `loadFundingSources` carries the fallback that keeps the funder selectable until the
 * backfill has run.
 */

import { persistedRuleRows } from '../../domain/services/modifierRules';
import {
  FUNDING_SOURCE_STATUS,
  carriesPriorAuthorization,
  isTelehealthModifier,
  namesRenderingProvider,
} from '../../domain/constants/fundingSource';

/**
 * The per-service fields carried across the boundary, stored name → model name.
 *
 * Extend this to add a per-service override; nothing else in the pair needs touching, and anything
 * absent from it does not survive a read-then-save.
 */
const coveredServiceFields = {
  id: 'treatmentServiceId',
  billWithoutAuthorization: 'billWithoutAuthorization',
  billsUnder: 'billsUnder',
  modifiers: 'modifiers',
  serviceCodeId: 'serviceCodeId',
  modifierRules: 'modifierRules',
};

/**
 * A payer's conditional modifier rows, normalized rather than trusted.
 *
 * THE SAME REASON `modifiers` IS NORMALIZED BELOW, one level deeper. The security rule types
 * `validTreatmentServices` as a list and says nothing about its elements, so every shape here is
 * something a document could hold: a row that is a string, a condition that is a number, a result
 * with no outcome. The editor spreads these into controls, so an unexpected shape makes the payer
 * unopenable rather than merely mis-rendered.
 *
 * AN UNRECOGNIZED CONDITION IS KEPT, NOT DROPPED, and that is the one place this is deliberately
 * permissive. Old clients persist: a row written by a newer editor against a fact this build has
 * never heard of will be read, shown and saved by this one, and dropping the condition on the way in
 * would quietly widen the rule to everybody the next time somebody pressed Save. Keeping it means
 * the resolver sees it, fails to match it, and holds — which somebody can act on. The editor marks
 * such a row rather than offering to edit it.
 */
const toModifierRules = (stored) => {
  // A LIST THAT IS NOT A LIST IS PRESERVED. Returning `[]` wrote "no rules" over a payer's
  // configuration on the next save, and the projector reads `[]` as nothing to apply — so the claim
  // goes out at the base tier instead of holding.
  if (!Array.isArray(stored)) {
    return stored;
  }

  const text = (value) => (typeof value === 'string' ? value : null);

  return stored.reduce((rows, row) => {
    // Kept for the same reason, one level down: dropping every row of a list leaves `[]`, which is
    // the same silent "no rules". The projector cannot read such a row either, so it holds.
    if (row === null || typeof row !== 'object' || Array.isArray(row)) {
      return rows.concat([row]);
    }

    // NOTHING IS DROPPED. Normalizing away a condition this build cannot read leaves the row asking
    // NOTHING, which is the catch-all: the editor then offers it, and the next save writes a rule
    // matching every performer where the payer named one tier. Unreadable is preserved so the
    // readers can see it — `?? []` is for an absent KEY, which genuinely means "asks nothing".
    const conditions = row.conditions ?? [];
    const modifiers = row.result?.modifiers ?? [];

    // NOT DEFAULTED TO `bill`. A row whose outcome is missing or unreadable is one neither side can
    // act on, and guessing the commonest answer makes the editor offer it for editing while the
    // resolver holds it — the screen and the claim disagreeing about the same row. Left as stored,
    // both treat it the same way: read-only here, held there.
    const result = { outcome: text(row.result?.outcome), modifiers };
    const reasonCode = text(row.result?.reasonCode);

    if (reasonCode !== null) {
      result.reasonCode = reasonCode;
    }

    return rows.concat([{ conditions, result }]);
  }, []);
};

/** `[{ id, … }]` → `[{ treatmentServiceId, … }]`, dropping entries that reference nothing. */
const toCoveredServices = (validTreatmentServices) => {
  return (validTreatmentServices ?? []).reduce((entries, entry) => {
    if (typeof entry?.id !== 'string' || entry.id.length === 0) {
      return entries;
    }

    const covered = Object.entries(coveredServiceFields).reduce((acc, [stored, model]) => {
      if (entry[stored] !== undefined) {
        acc[model] = entry[stored];
      }
      return acc;
    }, {});

    // Normalized here rather than trusted, because `modifiers` is the one entry in the map above that
    // is a LIST, and the security rule types `validTreatmentServices` as a list without saying
    // anything about the shape of its elements. The draft spreads this field, so a document holding a
    // number or an object would throw and leave that payer unopenable, and a string would be spread
    // into one modifier per character. The resolver already filters the same way on its own read.
    if (covered.modifiers !== undefined) {
      covered.modifiers = Array.isArray(covered.modifiers)
        ? covered.modifiers.filter((modifier) => typeof modifier === 'string' && modifier.trim().length > 0)
        : [];
    }

    // A blank reference names no code, and carrying it would read as an override to the editor and
    // as none to the projector.
    if (typeof covered.serviceCodeId !== 'string' || covered.serviceCodeId.trim().length === 0) {
      delete covered.serviceCodeId;
    }

    if (covered.modifierRules !== undefined) {
      covered.modifierRules = toModifierRules(covered.modifierRules);
    }

    return entries.concat([covered]);
  }, []);
};

/** `[{ treatmentServiceId, … }]` → `[{ id, … }]`. */
const toStoredCoveredServices = (coveredServices) => {
  return (coveredServices ?? []).reduce((entries, entry) => {
    if (typeof entry?.treatmentServiceId !== 'string' || entry.treatmentServiceId.length === 0) {
      return entries;
    }

    const stored = Object.entries(coveredServiceFields).reduce((acc, [storedName, model]) => {
      if (entry[model] !== undefined) {
        acc[storedName] = entry[model];
      }
      return acc;
    }, {});

    // A rule nobody started is dropped here, exactly as a codeless place override is: the draft
    // holds one from the moment somebody presses Add, and storing it would persist a row that asks
    // nothing and produces nothing. Doing it at the point of writing rather than in the editor is
    // what lets a blank row be the ordinary state of one just added.
    if (stored.modifierRules !== undefined) {
      stored.modifierRules = persistedRuleRows(stored.modifierRules);
    }

    return entries.concat([stored]);
  }, []);
};

/**
 * Place-of-service overrides, in and out.
 *
 * THE SAME SHAPE AS COVERED SERVICES and for the same reason: an entry is keyed on the id of the
 * place it references, with no minted row id, so one override per place is a property of the data
 * rather than a rule the editor has to remember. The stored and model names match here because the
 * field is net-new — there is no platform spelling to preserve, which is the only reason
 * `validTreatmentServices` and `coveredServices` differ.
 *
 * An entry with no code is DROPPED rather than stored as an empty override: absence already means
 * "the place keeps its own code", so a blank one is a row somebody opened and did not fill.
 */
const toPlaceOfServiceOverrides = (stored) => {
  return (stored ?? []).reduce((entries, entry) => {
    const code = typeof entry?.placeOfServiceCode === 'string' ? entry.placeOfServiceCode.trim() : '';

    if (typeof entry?.id !== 'string' || entry.id.length === 0 || code.length === 0) {
      return entries;
    }

    return entries.concat([{ placeOfServiceId: entry.id, placeOfServiceCode: code }]);
  }, []);
};

const toStoredPlaceOfServiceOverrides = (overrides) => {
  return (overrides ?? []).reduce((entries, entry) => {
    const code = typeof entry?.placeOfServiceCode === 'string' ? entry.placeOfServiceCode.trim() : '';

    if (typeof entry?.placeOfServiceId !== 'string' || entry.placeOfServiceId.length === 0
      || code.length === 0) {
      return entries;
    }

    return entries.concat([{ id: entry.placeOfServiceId, placeOfServiceCode: code }]);
  }, []);
};

/**
 * The document id for a version after the first: `{fundingSourceId}_{effectiveFrom}_{version}`.
 *
 * DERIVED, NEVER GENERATED, and that is what makes concurrent saves safe without a query inside a
 * transaction. Two writers computing the same version number aim at the same id, so the second write
 * lands on a document that already exists — which the rules read as an update and refuse. An auto-id
 * would let both succeed, and then TWO rows for one effective date would carry `isLatestVersion`.
 * That is worse than a refused write: the as-of query ends in `LIMIT 1`, so it would silently return
 * whichever of them Firestore reached first.
 *
 * The series' FIRST document keeps the auto-id it was created with, because that id is what every
 * other collection already references. So this scheme applies from version 2 onward, and an id is
 * only self-describing for documents written after versioning arrived.
 *
 * @param {string} fundingSourceId
 * @param {string} effectiveFrom - 'YYYY-MM-DD'
 * @param {number} version
 * @returns {string}
 */
export function versionDocumentId(fundingSourceId, effectiveFrom, version) {
  return `${fundingSourceId}_${effectiveFrom}_${version}`;
} // versionDocumentId()

/**
 * @param {Object} storedFundingSource - a `fundingSources` document
 * @returns {import('../../domain/model/FundingSource.types').FundingSource}
 */
export function toFundingSource(storedFundingSource) {
  return {
    versionId: storedFundingSource.id,
    // Its own id when unset: a document written before versioning is the head of its own series, and
    // this is the value the backfill writes.
    fundingSourceId: storedFundingSource.fundingSourceId ?? storedFundingSource.id,
    // No default — see the note above on why an unversioned funder gets no invented date.
    effectiveFrom: storedFundingSource.effectiveFrom ?? null,
    version: Number(storedFundingSource.version) || 1,
    isLatestVersion: storedFundingSource.isLatestVersion !== false,
    name: storedFundingSource.fundingSourceName ?? '',
    notes: storedFundingSource.notes ?? '',
    // Null is a real stored value on both axes, not a gap to be defaulted: the migration could not
    // decompose the legacy `other` and `regionalCenter` records and left them unset rather than
    // inventing a value. `?? null` normalizes an absent field to the same thing, so no reader has
    // to tell "deliberately unset" from "field not written".
    archetype: storedFundingSource.archetype ?? null,
    billingChannel: storedFundingSource.billingChannel ?? null,
    // Null where the channel is not adjudicated — an invoice has no authorization to bill without, so
    // the field is inapplicable rather than unanswered. Where it IS a question it defaults to FALSE,
    // including for every document written before the field existed: authorization is the norm, and
    // the two directions fail differently — a needless hold is visible and clearable, a claim for
    // unauthorized work is neither — so the default goes to the failure somebody notices.
    billWithoutAuthorization: carriesPriorAuthorization(storedFundingSource.billingChannel ?? null)
      ? storedFundingSource.billWithoutAuthorization === true
      : null,
    // NEVER DEFAULTED — read as stored, and null when nothing is stored. Who renders is a decision
    // about how a payer is billed, and `performer` is only the commonest answer, not a safe one: a
    // funder whose claims must go out under a supervising provider would have been silently marked
    // as billing under the performer, produced a claim naming an NPI the payer does not accept, and
    // shown nothing to say the choice had been made for it.
    //
    // NULL THEREFORE MEANS TWO THINGS, told apart by the channel. Where the channel names a renderer,
    // it means nobody has chosen yet, and the editor refuses to save until somebody does. Where it
    // does not, it means this funder's bills have nowhere to put a renderer — see the clear on write.
    billsUnder: namesRenderingProvider(storedFundingSource.billingChannel ?? null)
      ? storedFundingSource.billsUnder ?? null
      : null,
    coveredServices: toCoveredServices(storedFundingSource.validTreatmentServices),
    placeOfServiceOverrides: toPlaceOfServiceOverrides(storedFundingSource.placeOfServiceOverrides),
    // Read as stored, and null for anything outside the set. A payer that wants no telehealth
    // modifier and one nobody has configured are the same answer here, deliberately — see
    // TELEHEALTH_MODIFIERS for why there is no third state.
    telehealthModifier: isTelehealthModifier(storedFundingSource.telehealthModifier ?? null)
      ? storedFundingSource.telehealthModifier
      : null,
    // Older documents predate the status field — treat a missing status as active so nothing
    // silently disappears from the picker.
    isActive: (storedFundingSource.status ?? FUNDING_SOURCE_STATUS.ACTIVE)
      === FUNDING_SOURCE_STATUS.ACTIVE,
    isDeleted: storedFundingSource.isDeleted === true,
  };
} // toFundingSource()

/**
 * The editable record as a document, without the fields the write path owns.
 *
 * Takes the record ONLY — no id, no effective date, no version, no clinic, no `isLatestVersion`.
 * Those belong to whichever write is placing this version on the timeline, and keeping them out means
 * this cannot be handed a whole document and quietly write a version at the wrong date, or claim to
 * be the live entry for one.
 *
 * @param {{ name?: string, notes?: string, archetype?: string|null, billingChannel?: string|null,
 *   coveredServices?: import('../../domain/model/FundingSource.types').CoveredService[],
 *   isActive?: boolean }} record
 * @returns {Object}
 */
export function toDocument(record) {
  return {
    fundingSourceName: (record.name ?? '').trim(),
    notes: record.notes ?? '',
    archetype: record.archetype ?? null,
    billingChannel: record.billingChannel ?? null,
    // Cleared where the channel is not adjudicated, for the same reason `billsUnder` is: a funder
    // switched to invoice must not keep an exemption that silently reappears if it switches back.
    billWithoutAuthorization: carriesPriorAuthorization(record.billingChannel ?? null)
      ? record.billWithoutAuthorization === true
      : null,
    // Written as chosen where the channel names a rendering provider, CLEARED where it does not.
    // Nothing is defaulted on the way in — see the read side for why `performer` is not a safe
    // assumption. The clear still matters: a funder switched from electronic claims to invoice must
    // not carry a stale rule that reappears if it switches back, because that is a decision nobody
    // re-made.
    billsUnder: namesRenderingProvider(record.billingChannel ?? null)
      ? record.billsUnder ?? null
      : null,
    validTreatmentServices: toStoredCoveredServices(record.coveredServices),
    placeOfServiceOverrides: toStoredPlaceOfServiceOverrides(record.placeOfServiceOverrides),
    telehealthModifier: isTelehealthModifier(record.telehealthModifier ?? null)
      ? record.telehealthModifier
      : null,
    status: record.isActive === false
      ? FUNDING_SOURCE_STATUS.INACTIVE
      : FUNDING_SOURCE_STATUS.ACTIVE,
  };
} // toDocument()
